import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { trainerApi, type CourseMaterialDto } from '@/api/trainer'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  ArrowLeft, Link2, FileText, File, FileVideo,
  Download, ExternalLink, BookOpen,
} from 'lucide-react'
import { formatCourseDuration } from '@/lib/duration'

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isYouTubeUrl(url: string): boolean {
  return /(?:youtube\.com\/watch|youtu\.be\/)/.test(url)
}

function youTubeEmbedUrl(url: string): string | null {
  const m =
    url.match(/youtube\.com\/watch\?v=([^&]+)/) ??
    url.match(/youtu\.be\/([^?]+)/)
  return m ? `https://www.youtube.com/embed/${m[1]}` : null
}

function MaterialIcon({ contentType }: { contentType: string | null }) {
  if (!contentType) return <File size={16} className="text-gray-400" />
  if (contentType === 'application/pdf') return <FileText size={16} className="text-red-500" />
  if (contentType.startsWith('video/')) return <FileVideo size={16} className="text-purple-500" />
  return <File size={16} className="text-gray-400" />
}

// ─── Material row (read-only) ─────────────────────────────────────────────────

function MaterialRow({ material, courseId }: { material: CourseMaterialDto; courseId: string }) {
  const [expanded, setExpanded] = useState(false)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [loadingUrl, setLoadingUrl] = useState(false)

  const isLink = material.materialType === 'Link'
  const isEmbeddableYt = isLink && material.url && isYouTubeUrl(material.url)
  const isViewablePdf = !isLink && material.contentType === 'application/pdf'

  const fetchUrl = async () => {
    if (downloadUrl) return downloadUrl
    setLoadingUrl(true)
    try {
      const res = await trainerApi.getCourseMaterialDownloadUrl(courseId, material.id)
      setDownloadUrl(res.data.url)
      return res.data.url
    } finally {
      setLoadingUrl(false)
    }
  }

  const handleDownload = async () => {
    const url = await fetchUrl()
    window.open(url, '_blank', 'noopener')
  }

  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
        <div className="shrink-0">
          {isLink
            ? <Link2 size={16} className="text-indigo-500" />
            : <MaterialIcon contentType={material.contentType} />
          }
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{material.title}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {isLink
              ? material.url
              : `${material.fileName ?? 'File'}${material.fileSizeBytes ? ` · ${formatBytes(material.fileSizeBytes)}` : ''}`
            }
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {(isEmbeddableYt || isViewablePdf) && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-gray-500 hover:text-gray-700"
              onClick={() => setExpanded(v => !v)}>
              {expanded ? 'Hide' : 'Preview'}
            </Button>
          )}
          {isLink ? (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-indigo-600"
              onClick={() => window.open(material.url!, '_blank', 'noopener')} title="Open link">
              <ExternalLink size={14} />
            </Button>
          ) : (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400 hover:text-indigo-600"
              onClick={handleDownload} disabled={loadingUrl} title="Download">
              <Download size={14} />
            </Button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50">
          {isEmbeddableYt && material.url && (() => {
            const embedUrl = youTubeEmbedUrl(material.url)
            return embedUrl ? (
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={embedUrl}
                  className="absolute inset-0 w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen title={material.title}
                />
              </div>
            ) : null
          })()}
          {isViewablePdf && (
            <div className="p-3">
              {downloadUrl ? (
                <iframe src={downloadUrl} className="w-full rounded border border-gray-200"
                  style={{ height: 500 }} title={material.title} />
              ) : (
                <Button size="sm" variant="outline"
                  onClick={async () => { await fetchUrl(); setExpanded(true) }}
                  disabled={loadingUrl}>
                  {loadingUrl ? 'Loading…' : 'Load preview'}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function TrainerCourseDetailPage() {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()

  // Re-use the admin getCourse endpoint — trainers can read course details
  // (the materials endpoint is accessible to Trainer role)
  const { data: courses = [] } = useQuery({
    queryKey: ['trainer-courses'],
    queryFn: () => trainerApi.getMyCourses().then(r => r.data),
  })

  const course = courses.find(c => c.courseId === courseId)

  const { data: materials = [], isLoading } = useQuery({
    queryKey: ['course-materials', courseId],
    queryFn: () => trainerApi.getCourseMaterials(courseId!).then(r => r.data),
    enabled: !!courseId,
  })

  return (
    <div className="space-y-6 max-w-3xl">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 cursor-pointer"
      >
        <ArrowLeft size={14} /> Back
      </button>

      {/* Course header */}
      {course && (
        <div>
          <div className="flex items-start gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold text-gray-900">{course.name}</h1>
            {course.code && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 mt-1">
                {course.code}
              </span>
            )}
            {course.category && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200 mt-1">
                {course.category}
              </span>
            )}
          </div>
          {course.description && (
            <p className="text-sm text-gray-500 mt-2">{course.description}</p>
          )}
          <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-500">
            {formatCourseDuration(course) && <span>Duration: <strong>{formatCourseDuration(course)}</strong></span>}
            {course.defaultExpiryMonths != null && <span>Renews every: <strong>{course.defaultExpiryMonths} months</strong></span>}
          </div>
        </div>
      )}

      {/* Materials */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Course Materials</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-gray-400 py-4 text-center">Loading…</p>
          ) : materials.length === 0 ? (
            <div className="py-10 text-center">
              <BookOpen size={32} className="mx-auto text-gray-200 mb-3" />
              <p className="text-gray-500 font-medium text-sm">No materials yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Your administrator hasn't added any materials to this course yet.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {materials.map(m => (
                <MaterialRow key={m.id} material={m} courseId={courseId!} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
